import { apiClient } from './client'
import type { CapwayJob, Invoice, InvoiceDetail, InvoiceListResult, InvoiceListSort, InvoiceStatus, JobSchedule, JobStatus } from './types'

const CAPWAY_RUN_PATHS: Record<CapwayJob, string> = {
  capway_billing: '/invoices/capway/run-billing',
  capway_sync: '/invoices/capway/run-sync'
}

export interface ListInvoicesParams {
  search?: string
  customer_id?: number
  status?: InvoiceStatus
  from?: string
  to?: string
  sort?: InvoiceListSort
  limit?: number
  after?: number
  before?: number
}

const EMPTY_SUMMARY: InvoiceListResult['summary'] = { invoiced: '0', paid: '0', outstanding: '0' }

function normalizeListResult(data: unknown): InvoiceListResult {
  if (Array.isArray(data)) {
    const invoices = data as Invoice[]

    return { invoices, total: invoices.length, summary: EMPTY_SUMMARY, has_next: false, has_previous: false }
  }

  if (data && typeof data === 'object' && 'invoices' in data) {
    const result = data as Partial<InvoiceListResult>
    const invoices = Array.isArray(result.invoices) ? result.invoices : []

    return {
      invoices,
      total: typeof result.total === 'number' ? result.total : 0,
      summary: result.summary ?? EMPTY_SUMMARY,
      has_next: result.has_next === true,
      has_previous: result.has_previous === true
    }
  }

  return { invoices: [], total: 0, summary: EMPTY_SUMMARY, has_next: false, has_previous: false }
}

export async function listInvoicePage(params: ListInvoicesParams = {}, signal?: AbortSignal): Promise<InvoiceListResult> {
  const { data } = await apiClient.get<unknown>('/invoices', { params, signal })

  return normalizeListResult(data)
}

export async function listInvoices(params: ListInvoicesParams = {}, signal?: AbortSignal): Promise<Invoice[]> {
  const result = await listInvoicePage(params, signal)

  return result.invoices
}

function normalizeInvoiceDetail(data: InvoiceDetail): InvoiceDetail {
  return { ...data, lines: Array.isArray(data.lines) ? data.lines : [], events: Array.isArray(data.events) ? data.events : [] }
}

export async function getInvoiceById(id: number): Promise<InvoiceDetail> {
  const { data } = await apiClient.get<InvoiceDetail>(`/invoices/${id}`)

  return normalizeInvoiceDetail(data)
}

export async function markInvoiceRefunded(id: number): Promise<InvoiceDetail> {
  const { data } = await apiClient.post<InvoiceDetail>(`/invoices/${id}/mark_refunded`)

  return normalizeInvoiceDetail(data)
}

export async function voidInvoice(id: number): Promise<InvoiceDetail> {
  const { data } = await apiClient.post<InvoiceDetail>(`/invoices/${id}/void`)

  return normalizeInvoiceDetail(data)
}

export async function listCapwayJobStatuses(): Promise<JobStatus<CapwayJob>[]> {
  const { data } = await apiClient.get<JobStatus<CapwayJob>[]>('/invoices/capway/runs')

  return data
}

export async function runCapwayJob(job: CapwayJob): Promise<void> {
  await apiClient.post(CAPWAY_RUN_PATHS[job])
}

export async function updateCapwaySchedule(job: CapwayJob, cron: string): Promise<JobSchedule> {
  const { data } = await apiClient.patch<JobSchedule>(`/invoices/capway/schedules/${job}`, { cron })

  return data
}
